import Vue from 'vue'
import store from '@/store'
import api from '@/api'
import { BYOK_REQUIRED_MESSAGE } from '@/utils/aiErrors'

export const SUBSCRIPTION_FEATURES = [
  {
    id: 'ask',
    title: 'Ask a bookmark a question',
    text:
      'Ask what a saved page is about and get an answer from the page itself.'
  },
  {
    id: 'summary',
    title: 'Summarise a long page',
    text: 'Turn a site into a short summary you can read before you open it.'
  },
  {
    id: 'recommendations',
    title: 'Find similar websites',
    text: 'See recommendations based on the bookmarks you already keep.'
  },
  {
    id: 'organise',
    title: 'Organise your bookmarks',
    text: 'Let AI suggest clearer categories for the pages you have saved.'
  },
  {
    id: 'categories',
    title: 'Ask across a category',
    text:
      'Put one question to the bookmarks in a category, instead of opening each page.'
  },
  {
    id: 'workspace',
    title: 'Ask across a workspace',
    text: 'Search the bookmarks saved in a workspace with a single question.'
  }
]

export const BYOK_STEPS = [
  'Open Profile, then Subscription',
  'Paste your key under Bring Your Own Key',
  'Save it, then try this feature again'
]

const DISMISS_MS = 18000
const ROTATE_MS = 4500

export const aiAccessNotice = Vue.observable({
  visible: false,
  mode: null,
  featureIndex: 0,
  session: 0,
  shownAt: 0
})

let hideTimer = null
let rotateTimer = null
let remaining = DISMISS_MS
let startedAt = 0
let paused = false

const clearTimers = () => {
  clearTimeout(hideTimer)
  clearInterval(rotateTimer)
  hideTimer = null
  rotateTimer = null
}

const startTimers = () => {
  clearTimers()
  if (!aiAccessNotice.visible || paused) return

  startedAt = Date.now()
  hideTimer = setTimeout(() => {
    dismissAiAccessNotice()
  }, remaining)

  if (aiAccessNotice.mode === 'subscription') {
    rotateTimer = setInterval(() => {
      aiAccessNotice.featureIndex =
        (aiAccessNotice.featureIndex + 1) % SUBSCRIPTION_FEATURES.length
    }, ROTATE_MS)
  }
}

const openNotice = mode => {
  const now = Date.now()
  const alreadyOpen = aiAccessNotice.visible && aiAccessNotice.mode === mode
  if (alreadyOpen && now - aiAccessNotice.shownAt < 400) return

  aiAccessNotice.mode = mode
  aiAccessNotice.visible = true
  aiAccessNotice.shownAt = now
  aiAccessNotice.session += 1
  remaining = DISMISS_MS
  paused = false
  startTimers()
}

export function showSubscriptionNotice(featureId) {
  const index = SUBSCRIPTION_FEATURES.findIndex(item => item.id === featureId)
  if (index >= 0) {
    aiAccessNotice.featureIndex = index
  }
  openNotice('subscription')
}

export function showByokNotice() {
  openNotice('byok')
}

const LOGIN_PROMPT_KEY = 'linkbook-ai-access-prompted'
let loginPromptStarted = false

export function resetAiAccessLoginPrompt() {
  loginPromptStarted = false
}

const hasPaidSubscription = () => {
  const permissions = store.getters.permissions || {}
  return Boolean(
    permissions.subscriptionStarter ||
      permissions.subscriptionPro ||
      permissions.subscriptionBusiness ||
      permissions.subscriptionEnterprise
  )
}

export async function promptAiAccessAfterLogin() {
  if (loginPromptStarted) return
  if (typeof sessionStorage === 'undefined') return
  if (sessionStorage.getItem(LOGIN_PROMPT_KEY)) return

  loginPromptStarted = true

  if (!hasPaidSubscription()) {
    sessionStorage.setItem(LOGIN_PROMPT_KEY, 'subscription')
    showSubscriptionNotice()
    return
  }

  try {
    const status = await api.getOpenRouterKeyStatus()
    if (!store.getters.isLoggedIn) return
    sessionStorage.setItem(LOGIN_PROMPT_KEY, 'byok')
    if (!status?.hasKey) {
      showByokNotice()
    }
  } catch (error) {
    loginPromptStarted = false
  }
}

export function presentAiAccessError(error, featureId) {
  const code = error?.code || error?.response?.data?.code
  if (code === 'SUBSCRIPTION_REQUIRED') {
    showSubscriptionNotice(featureId)
    return true
  }
  if (code === 'BYOK_REQUIRED') {
    showByokNotice()
    return true
  }
  return false
}

export function dismissAiAccessNotice() {
  clearTimers()
  paused = false
  remaining = DISMISS_MS
  aiAccessNotice.visible = false
}

export function pauseAiAccessNotice() {
  if (!aiAccessNotice.visible || paused) return
  paused = true
  remaining = Math.max(0, remaining - (Date.now() - startedAt))
  clearTimers()
}

export function resumeAiAccessNotice() {
  if (!aiAccessNotice.visible || !paused) return
  paused = false
  if (remaining <= 0) {
    dismissAiAccessNotice()
    return
  }
  startTimers()
}

export function isAiAccessError(error) {
  const code = error?.code || error?.response?.data?.code
  return code === 'SUBSCRIPTION_REQUIRED' || code === 'BYOK_REQUIRED'
}

export async function ensureAiClientAccess(featureId) {
  const permissions = store.getters.permissions || {}
  const hasSubscription =
    permissions.subscriptionStarter ||
    permissions.subscriptionPro ||
    permissions.subscriptionBusiness ||
    permissions.subscriptionEnterprise

  if (!hasSubscription) {
    const error = new Error(
      'AI features require an active LinkBook subscription.'
    )
    error.code = 'SUBSCRIPTION_REQUIRED'
    presentAiAccessError(error, featureId)
    throw error
  }

  const status = await api.getOpenRouterKeyStatus()
  if (!status.hasKey) {
    const error = new Error(BYOK_REQUIRED_MESSAGE)
    error.code = 'BYOK_REQUIRED'
    presentAiAccessError(error, featureId)
    throw error
  }
}
